import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  HStack,
  Stack,
  Progress,
  Divider,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { 
  FiMessageSquare, 
  FiPhoneCall, 
  FiStar 
} from 'react-icons/fi';
import { 
  MdOutlineChat, 
  MdOutlineMarkEmailRead, 
  MdHeadsetMic 
} from 'react-icons/md';
import { formatTime } from '../utils/formatters';

/**
 * CanaisAtendimento - Component for displaying service channel statistics
 * 
 * @param {Object} props - Component props
 * @param {Array} props.canais - Array of channel objects
 * @param {boolean} props.isLoading - Loading state
 */
const CanaisAtendimento = ({ canais, isLoading }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  
  // Map icon names to actual icon components
  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'FiMessageSquare':
        return FiMessageSquare;
      case 'MdOutlineMarkEmailRead':
        return MdOutlineMarkEmailRead;
      case 'FiPhoneCall':
        return FiPhoneCall;
      case 'MdHeadsetMic':
        return MdHeadsetMic;
      default:
        return MdOutlineChat;
    }
  };

  // Find the most efficient channel
  const getMostEfficientChannel = () => {
    if (!canais || canais.length === 0) return null;
    
    // This is a simplified example - in real-world, you might have a more complex calculation
    // Here we're using the first channel as the "most efficient", but this should be calculated
    return canais[0];
  };

  const mostEfficientChannel = getMostEfficientChannel();

  return (
    <Card bg={cardBg} boxShadow="md">
      <CardHeader pb={2}>
        <Flex align="center">
          <Icon as={MdOutlineChat} mr={2} />
          <Heading size="md">Canais de Atendimento</Heading>
        </Flex>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner />
          </Flex>
        ) : (
          <Stack spacing={4}>
            {canais.map((canal, index) => (
              <Box key={index}>
                <Flex justify="space-between" mb={1}>
                  <HStack>
                    <Icon as={getIconComponent(canal.icon)} color={canal.color} />
                    <Text fontWeight="medium">{canal.nome}</Text>
                  </HStack>
                  <Text fontWeight="bold">{canal.percentual}%</Text>
                </Flex>
                <Progress value={canal.percentual} size="sm" colorScheme={canal.color.split('.')[0]} borderRadius="full" />
                <Flex justify="space-between" fontSize="xs" color="gray.500" mt={1}>
                  <Text>{canal.atendimentos} atendimentos</Text>
                  <Text>Tempo médio: {formatTime(canal.tempoMedio)}</Text>
                </Flex>
              </Box>
            ))}
            
            <Divider />
            
            {mostEfficientChannel && (
              <Flex justify="space-between" fontSize="sm">
                <HStack>
                  <Icon as={FiStar} color="orange.400" />
                  <Text fontWeight="medium">Canal mais eficiente:</Text>
                </HStack>
                <Text fontWeight="bold">{mostEfficientChannel.nome} (93% satisfação)</Text>
              </Flex>
            )}
          </Stack>
        )}
      </CardBody>
    </Card>
  );
};

export default CanaisAtendimento;